import { ContextWindow } from "@asup/context-menu";
import { AsyncButton } from "aic";
import { useTlfContext } from "contexts/AppSettingsContext";
import { getVariantPatchContext } from "contexts/tlf/getVariantPatchContext";
import {
  ACTIVATE_VARIANT_PATCH,
  REVERT_TO_VARIANT_MASTER,
  SET_CREATE_VARIANT_PATCH_WINDOW,
  TOGGLE_VARIANT_PATCH,
  TRACK_ACTIVE_VARIANT_PATCH,
} from "contexts/tlf/interface";
import { getVariantPatchGroupName } from "functions/tlf/getVariantPatchGroupName";
import { groupVariantPatches } from "functions/tlf/groupVariantPatches";
import { ReactNode, useEffect } from "react";
import { Alert, Container } from "react-bootstrap";
import { CreatePatchWindow } from "./CreatePatchWindow";
import { TlfVariantPatchRow } from "./TlfVariantPatchRow";

/**
 * Hosts the separate patch ContextWindow and applies named patches to the full TLF item.
 *
 * @returns The patch sidebar and its create/manage windows
 */
export const TlfArsPatchSidebar = (): ReactNode => {
  const tlfContext = useTlfContext();
  const {
    activeVariantPatchName,
    activeVariantPatchInvalidOps,
    appliedVariantPatches,
    dispatch,
    variantCreationView,
  } = tlfContext;
  const { master, currentMaster } = getVariantPatchContext(tlfContext);
  const patchGroups =
    master === null || currentMaster === null
      ? []
      : groupVariantPatches(currentMaster).map((patchNames) => ({
          patchNames,
          name: getVariantPatchGroupName(currentMaster, patchNames),
        }));

  useEffect(() => {
    dispatch({
      operation: TRACK_ACTIVE_VARIANT_PATCH,
      calledFrom: "TlfArsPatchSidebar:trackActivePatch",
    });
  }, [
    activeVariantPatchName,
    appliedVariantPatches,
    dispatch,
    tlfContext.masterTlfData,
    tlfContext.tlfData,
  ]);

  return (
    <>
      <ContextWindow
        id="tlf-ars-patches-window"
        title="Template patches"
        visible={patchGroups.length > 0}
        initialDockEdge="right"
        style={{ width: "360px", maxWidth: "90vw", maxHeight: "80vh" }}
      >
        <Container className="p-3 d-flex flex-column gap-2">
          <div className="d-flex flex-wrap gap-2">
            <AsyncButton
              id="tlf-create-new-patch"
              size="sm"
              variant="primary"
              onClick={() =>
                dispatch({
                  operation: SET_CREATE_VARIANT_PATCH_WINDOW,
                  show: true,
                  calledFrom: "TlfArsPatchSidebar:openCreatePatch",
                })
              }
            >
              Create new patch
            </AsyncButton>
            {(variantCreationView || appliedVariantPatches.length > 0) && (
              <AsyncButton
                id="tlf-revert-to-master"
                size="sm"
                variant="outline-secondary"
                disabled={activeVariantPatchInvalidOps.length > 0}
                onClick={() =>
                  dispatch({
                    operation: REVERT_TO_VARIANT_MASTER,
                    calledFrom: "TlfArsPatchSidebar:revertToMaster",
                  })
                }
              >
                Revert to master
              </AsyncButton>
            )}
          </div>
          {patchGroups.length === 0 ? (
            <div className="text-muted">No patches have been created</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {patchGroups.map(({ patchNames: group, name }) => (
                <div
                  key={group[0]}
                  role="group"
                  aria-label={name}
                  className="d-flex flex-column gap-2 border-bottom pb-2"
                >
                  <h6 className="mb-0 text-break">{name}</h6>
                  {group.map((patchId) => (
                    <TlfVariantPatchRow
                      key={patchId}
                      patchId={patchId}
                      onToggleApplied={(applied) =>
                        dispatch({
                          operation: TOGGLE_VARIANT_PATCH,
                          patchName: patchId,
                          applied,
                          calledFrom: "TlfArsPatchSidebar:togglePatch",
                        })
                      }
                      onActivate={() =>
                        dispatch({
                          operation: ACTIVATE_VARIANT_PATCH,
                          patchName: patchId,
                          calledFrom: "TlfArsPatchSidebar:activatePatch",
                        })
                      }
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
          {activeVariantPatchInvalidOps.length > 0 && (
            <Alert
              variant="danger"
              className="mb-0"
            >
              Patch changes must use remove or replace operations:{" "}
              {activeVariantPatchInvalidOps.map((op) => `${op.op} ${op.path}`).join(", ")}
            </Alert>
          )}
        </Container>
      </ContextWindow>
      <CreatePatchWindow />
    </>
  );
};

TlfArsPatchSidebar.displayName = "TlfArsPatchSidebar";
